import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { DeleteIcon } from '../../icons'
import { Divider } from '../divider'
import { FieldError } from '../field-error'
import { HStack } from '../hstack'
import { Icon } from '../icon'
import { Pressable } from '../pressable'
import { VStack } from '../vstack'

export interface EditableListValidation {
    isError?: boolean
    errorMessage?: ReactNode
}

export interface EditableListItemProps<T> {
    item: T
    index: number
    isLast: boolean
    onChangeItem(value: T): void
}

export interface EditableListProps<T> {
    value: T[]
    itemRenderer(props: EditableListItemProps<T>): ReactNode
    onChange(value: T[]): void
    /** Per-row error, indexed alongside `value`. */
    validation?: EditableListValidation[]
    /** Draws a rule between rows. */
    separated?: boolean
    removeAccessibilityLabel?: string
    className?: string
}

/**
 * Repeating form rows with a per-row remove control — the caller supplies the
 * row body through `itemRenderer` and stays the owner of the list.
 *
 * Presentational and fully controlled: no add button and no empty-row seeding,
 * because those are policy the form owns (see the legacy `LabeledEditableList`,
 * which layers a label, an add control and empty-item pruning on top).
 */
export function EditableList<T>({
    value,
    itemRenderer,
    onChange,
    validation = [],
    separated = false,
    removeAccessibilityLabel,
    className,
}: EditableListProps<T>) {
    const remove = (index: number) => onChange(value.filter((_, i) => i !== index))

    const changeItem = (index: number, next: T) =>
        onChange(value.map((item, i) => (i === index ? next : item)))

    return (
        <VStack className={twMerge('gap-4', className)}>
            {value.map((item, index) => {
                const isLast = index === value.length - 1
                const rowError = validation[index]
                return (
                    <VStack key={index} className='gap-2'>
                        <HStack className='items-start gap-2'>
                            <VStack className='min-w-0 flex-1'>
                                {itemRenderer({
                                    item,
                                    index,
                                    isLast,
                                    onChangeItem: next => changeItem(index, next),
                                })}
                            </VStack>
                            <Pressable
                                accessibilityRole='button'
                                accessibilityLabel={removeAccessibilityLabel}
                                onPress={() => remove(index)}
                                className='h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-background-surface-hover'
                            >
                                <Icon as={DeleteIcon} size={24} className='text-content-muted' />
                            </Pressable>
                        </HStack>
                        {rowError?.isError && rowError.errorMessage ? (
                            <FieldError>{rowError.errorMessage}</FieldError>
                        ) : null}
                        {separated && !isLast ? <Divider /> : null}
                    </VStack>
                )
            })}
        </VStack>
    )
}

EditableList.displayName = 'EditableList'
